import { useEffect, useMemo, useRef } from 'react';
import { useApplicationContext } from '../contexts/ApplicationContext';
import { useApolloClient, useQuery } from '@apollo/client';
import { GetRentalApplicationIncomeDocsDocument } from '../graphql/queries';
import { useCappsRenterDocumentsWebApiHandler_setDocumentRedactionStatus } from '@zg-rentals/ts-capps-client-react-query';

const MAX_REFETCH_ATTEMPTS = 10;
// Less than 1 second is forbiden by the API spec
const REFETCH_INTERVAL = 6000;

/**
 * Keeps polling redaction related fields for all income documents and returns the doc array
 * Feel free to call this function from multiple components at the same time, because it is cached, it won't refetch unnecessarily
 */
export function useIncomeDocsRedaction() {
  // The refetch counter resets on any unmount/remount
  const refetchCounter = useRef(MAX_REFETCH_ATTEMPTS);
  const applicationId = useApplicationContext().application.id;
  const apolloClient = useApolloClient();
  const { data } = useQuery(GetRentalApplicationIncomeDocsDocument, {
    pollInterval: REFETCH_INTERVAL,
    onCompleted: () => {
      refetchCounter.current--;
    },
    skipPollAttempt: () => refetchCounter.current <= 0,
    skip: !applicationId,
    variables: {
      applicationId: applicationId!,
    },
  });

  const docIds = data?.rentalApplication?.answers?.currentIncomes
    ?.flatMap((x) => x?.documents)
    .map((x) => x?.id)
    .join(',');
  useEffect(() => {
    // Reset the refetch counter when set of document ids changes, this detects any new document
    // addition including when they happen on separate devices
    refetchCounter.current = MAX_REFETCH_ATTEMPTS;
  }, [docIds]);

  const { mutate: setDocumentRedactionStatus } = useCappsRenterDocumentsWebApiHandler_setDocumentRedactionStatus({
    meta: { context: { api: 'cappsRenterDocuments', details: 'setDocumentRedactionStatus' } },
    onSuccess: async () => {
      await apolloClient.refetchQueries({ include: [GetRentalApplicationIncomeDocsDocument] });
    },
  });

  const allDocs = useMemo(() => data?.rentalApplication?.answers?.currentIncomes?.flatMap((x) => x?.documents), [data]);

  const allDocsDoneProcessing = allDocs?.map((x) => x?.isRedactionCheckComplete).every((x) => x === true);

  useEffect(() => {
    if (allDocsDoneProcessing) {
      // stop polling early
      refetchCounter.current = 0;
    }
  }, [allDocsDoneProcessing]);

  useEffect(() => {
    const documentsToRedact = allDocs?.filter((x) => x?.isRedactionCheckComplete && x.canBeRedacted && !x.isRedacted);
    const idToRedact = documentsToRedact?.[0]?.id;
    if (!idToRedact) {
      return;
    }

    // We redact one document at a time, with each refetch
    // we'll move to the next document to redact since the previous one
    // will have isRedacted set to true

    // TODO: Update Web Api to support redaction of multiple docs and return
    // cache-friendly data on the mutation
    setDocumentRedactionStatus({
      postData: {
        documentId: idToRedact,
        isRedacted: true,
      },
    });
  }, [allDocs]);
  return allDocs;
}
